import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../components/context/AuthContext';
import {
  isPublicProductsSubscriptionRequired,
  getPublicProductsSubscriptionAmount,
  getPublicProductsSubscriptionCurrency,
} from '../config/publicProductsSubscription';
import { initiatePublicProductsSubscriptionPayment } from '../services/publicProductsSubscriptionPayment';
import { createLogger } from '../utils/logger';

const logger = createLogger('usePublicProductsSubscription');

/**
 * Subscription gate for individual product analytics pages (/products/analysis/:slug).
 * The /products catalog is public; this hook is not used there.
 */
export default function usePublicProductsSubscription() {
  const { currentUser, loading: authLoading, userRole, userId, userDocId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const subscriptionAmount = getPublicProductsSubscriptionAmount();
  const subscriptionCurrency = getPublicProductsSubscriptionCurrency();
  const subscriptionGateEnabled = isPublicProductsSubscriptionRequired();
  const roleBypassesSubscription = userRole === 'Admin';
  const mustSubscribe = subscriptionGateEnabled && !roleBypassesSubscription;

  const [analyticsUnlocked, setAnalyticsUnlocked] = useState(() =>
    isPublicProductsSubscriptionRequired() ? null : true
  );
  const [payingSubscription, setPayingSubscription] = useState(false);
  const [subscriptionPayError, setSubscriptionPayError] = useState('');

  useEffect(() => {
    if (!subscriptionGateEnabled || roleBypassesSubscription) {
      setAnalyticsUnlocked(true);
      return;
    }
    if (authLoading) return;
    if (!currentUser) {
      setAnalyticsUnlocked(false);
      return;
    }
    if (!userDocId) {
      setAnalyticsUnlocked(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'Users', userDocId));
        if (cancelled) return;
        const ok = snap.exists() && snap.data()?.products_subscription_active === true;
        setAnalyticsUnlocked(ok);
      } catch (err) {
        logger.error('Failed to read subscription status:', err);
        if (!cancelled) setAnalyticsUnlocked(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    subscriptionGateEnabled,
    roleBypassesSubscription,
    authLoading,
    currentUser,
    userDocId,
    location.pathname,
    location.key,
  ]);

  const handleSubscriptionPay = useCallback(async () => {
    setSubscriptionPayError('');
    if (!currentUser || !userId || !userDocId) {
      setSubscriptionPayError('Please sign in first.');
      return;
    }
    setPayingSubscription(true);
    try {
      await initiatePublicProductsSubscriptionPayment({
        identityId: userId,
        firestoreUserDocId: userDocId,
        navigate,
      });
    } catch (err) {
      logger.error('Subscription payment start failed:', err);
      setSubscriptionPayError(err.message || 'Could not start payment.');
    } finally {
      setPayingSubscription(false);
    }
  }, [currentUser, userId, userDocId, navigate]);

  const resolvingAccess = mustSubscribe && (authLoading || analyticsUnlocked === null);

  return {
    analyticsUnlocked,
    mustSubscribe,
    resolvingAccess,
    subscriptionAmount,
    subscriptionCurrency,
    payingSubscription,
    subscriptionPayError,
    handleSubscriptionPay,
    currentUser,
  };
}
